if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopThumbnailMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.setupVerticalThumbnailNavigation();
      }

      isVerticalThumbnailLayout() {
        return this.dataset.desktopLayout === 'thumbnail_slider' && this.desktopThumbnailMql.matches;
      }

      setupVerticalThumbnailNavigation() {
        const thumbnailSlider = this.elements.thumbnails;
        const slider = thumbnailSlider.slider;
        const previousButton = thumbnailSlider.querySelector('button[name="previous"]');
        const nextButton = thumbnailSlider.querySelector('button[name="next"]');
        if (!slider || !previousButton || !nextButton) return;

        const updateButtons = () => {
          if (!this.isVerticalThumbnailLayout()) return;
          const atStart = slider.scrollTop <= 1;
          const atEnd = slider.scrollTop + slider.clientHeight >= slider.scrollHeight - 1;
          if (previousButton.disabled !== atStart) previousButton.disabled = atStart;
          if (nextButton.disabled !== atEnd) nextButton.disabled = atEnd;
        };

        const scrollVertically = (event) => {
          if (!this.isVerticalThumbnailLayout()) return;
          event.preventDefault();
          event.stopImmediatePropagation();

          const firstThumbnail = slider.querySelector('.thumbnail-list__item');
          if (!firstThumbnail) return;
          const gap = parseFloat(window.getComputedStyle(slider).rowGap) || 0;
          const step = Number(event.currentTarget.dataset.step) || 1;
          const direction = event.currentTarget.name === 'next' ? 1 : -1;
          slider.scrollBy({
            top: direction * step * (firstThumbnail.offsetHeight + gap),
            behavior: 'smooth',
          });
        };

        previousButton.addEventListener('click', scrollVertically, true);
        nextButton.addEventListener('click', scrollVertically, true);
        slider.addEventListener('scroll', updateButtons, { passive: true });
        this.desktopThumbnailMql.addEventListener('change', () => {
          if (this.isVerticalThumbnailLayout()) {
            updateButtons();
          } else {
            slider.scrollTop = 0;
            thumbnailSlider.initPages();
          }
        });

        const buttonObserver = new MutationObserver(() => {
          if (this.isVerticalThumbnailLayout()) window.requestAnimationFrame(updateButtons);
        });
        buttonObserver.observe(previousButton, { attributes: true, attributeFilter: ['disabled'] });
        buttonObserver.observe(nextButton, { attributes: true, attributeFilter: ['disabled'] });
        window.requestAnimationFrame(updateButtons);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isVerticalThumbnailLayout()) {
          const slider = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          if (thumbnailTop < slider.scrollTop) {
            slider.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          } else if (thumbnailBottom > slider.scrollTop + slider.clientHeight) {
            slider.scrollTo({ top: thumbnailBottom - slider.clientHeight, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
